import React, { useEffect, useRef, useState } from 'react';
import { Text } from '@gjensidige/builders-components';
import {
  CalculationWidget,
  CalculationWidgetProps,
  formatAddressLabel,
  type AutocompleteAddressOption
} from '@gjensidige/se-common-calculation';
import { useFormValidation } from '@gjensidige/se-form-validation';
import type { ValidationRule, FieldValidation, FieldType, CommonValidationParams } from '@gjensidige/se-form-validation';

import { getAddressDetails } from '../api-config/index';
import { useAddressAutocomplete } from '../hooks/useAddressAutocomplete';
import { WidgetProps } from '../interfaces/Types';
import { AppHelper } from '../utils/AppHelper';

interface InputWidgetProps {
  enonicData: WidgetProps;
}
interface FormData {
  ssn: string;
  phone: string;
  houseType: string;
  autocompleteAddress: {
    formatted: string;
    zipCode: string;
    objektidentitet: string;
  };
}

export const InputWidgetContainer: React.FC<InputWidgetProps> = ({
  enonicData
}: InputWidgetProps) => {
  const [formData, setFormData] = useState<FormData>({
    ssn: '',
    phone: '',
    houseType: '',
    autocompleteAddress: {
      formatted: '',
      zipCode: '',
      objektidentitet: ''
    }
  });
  const [addressSearchQuery, setAddressSearchQuery] = useState('');
  const [nameFromService, setNameFromService] = useState({
    calculationStatus: {
      error: false
    },
    customerName: '',
  });

  const ADDRESS_SEARCH_MIN_LENGTH = 3;
  const MIN_AGE = 18;
  const NEXT_PAGE_URL_PATH = '/links/webshop-home';

  // Address autocomplete
  const {
    data: addressOptions = [] as AutocompleteAddressOption[],
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses
  } = useAddressAutocomplete(addressSearchQuery, {
    minSearchLength: ADDRESS_SEARCH_MIN_LENGTH,
    staleTime: 30000
  });

  const addressComboOptions = addressOptions.map((option: AutocompleteAddressOption) => ({
    label: formatAddressLabel(option),
    value: option.objektidentitet
  }));

  const inputFieldDefinitions = [
    {
      fieldType: 'ssn',
      fieldName: 'Personnummer',
      validation: {
        rules: ['required', 'ssn', 'ssnAge'] as ValidationRule[],
        validationParams: {
          minAge: MIN_AGE,
          allowCoordinationNumber: enonicData?.channel === 'rikshem' || enonicData?.channel === 'hgf'
        } as CommonValidationParams,
        validateOnChange: true
      },
      ui: {
        id: 'cpr-number',
        labelText: enonicData.cprNumberLabel,
        name: 'input-cprNumber',
        className: 'mt-1',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'ssn',
        helpText: '12 siffror ÅÅÅÅMMDDNNNN.',
        isHelpTextAlwaysVisible: true,
        inputMode: 'numeric' as const
      }
    },
    {
      fieldType: 'autocompleteAddress',
      fieldName: 'Adress',
      validation: {
        rules: ['required', 'autocompleteAddress'] as ValidationRule[],
        validationParams: {
          addressSearchQuery: addressSearchQuery,
          availableOptions: addressOptions
        } as CommonValidationParams,
        validateOnChange: true
      },
      ui: {
        id: 'gatuadress',
        labelText: enonicData.addressLabel,
        name: 'input-gatuadress',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'street-address',
        autoCompleteLabel: 'street-address',
        placeholder: 'Fyll i och välj från listan',
        helpText: 'Sök efter gatunamn och nummer.',
        isHelpTextAlwaysVisible: true,
      }
    },
    {
      fieldType: 'phone',
      fieldName: 'Mobil',
      validation: {
        rules: ['phone'] as ValidationRule[]
      },
      ui: {
        id: 'phone-number',
        labelText: 'Mobil (frivilligt)',
        name: 'input-phoneNumber',
        labelClassName: 'input-label-text',
        inputWrapperClassName: 'phone-number',
        helpText: AppHelper.phoneNrHelpText,
        helpTextToggleLabel: 'Visa hjälptext för Mobil',
        helpTextCloseLabel: 'Stäng hjälptext för Mobil',
        inputMode: 'numeric' as const,
        autoCompleteLabel: 'tel'
      }
    }
  ] as const;

  const houseTypeOptions = [
    {
      id: 'rent',
      labelText: enonicData.tenancyLabel,
      value: '1'
    },
    {
      id: 'owned',
      labelText: enonicData.ownedLabel,
      value: '2'
    }
  ];

  const fieldConfigs: FieldValidation[] = [
    {
      fieldType: 'houseType' as FieldType,
      fieldName: 'Bostadstyp',
      rules: ['required'] as ValidationRule[],
      customMessage: 'Du måste välja en bostadstyp.'
    },
    ...inputFieldDefinitions.map(field => {
      return {
        fieldType: field.fieldType,
        fieldName: field.fieldName,
        rules: field.validation.rules,
        ...('validationParams' in field.validation && field.validation.validationParams && {
          validationParams: field.validation.validationParams
        }),
        ...('validateOnChange' in field.validation && field.validation.validateOnChange && {
          validateOnChange: field.validation.validateOnChange,
        })
      };
    })
  ];

  const {
    refs,
    handleBlur,
    handleChange,
    validateAll,
    getFieldError,
    hasFieldError,
  } = useFormValidation(fieldConfigs);

  useEffect(() => {
    if (enonicData?.selectedHouseType === 'hyresratt' || enonicData?.selectedHouseType === 'bostadsratt') {
      const houseTypeValue = enonicData.selectedHouseType === 'hyresratt' ? '1' : '2';
      setFormData(prev => ({ ...prev, houseType: houseTypeValue }));
    }
  }, [enonicData?.selectedHouseType]);

  useEffect(() => {
    if ((addressSearchQuery.length >= ADDRESS_SEARCH_MIN_LENGTH && addressOptions.length === 0) && (!isLoadingAddresses && !isFetchingAddresses)) {
      handleBlur('autocompleteAddress', addressSearchQuery);
    }
  }, [addressSearchQuery, addressOptions, isLoadingAddresses, isFetchingAddresses]);

  const getNameFromSSN = async (validSSN: string) => {
    const nameFromSSN = await getAddressDetails(Number(validSSN));
    if (nameFromSSN && !nameFromSSN.calculationStatus.error) {
      setNameFromService(nameFromSSN);
    };
  }

  const createChangeHandler = (fieldType: FieldType) => (event: any) => {
    let value: string;

    if (fieldType === 'autocompleteAddress') {
      const selectedValue = event[0];
      const selectedOption = addressOptions.find((opt: AutocompleteAddressOption) => opt.objektidentitet === selectedValue?.value?.trim());
      if (selectedOption) {
        const formattedAddress = formatAddressLabel(selectedOption);
        setFormData(prev => ({
          ...prev,
          autocompleteAddress: {
            formatted: formattedAddress,
            zipCode: selectedOption?.adressComponents?.postnummer?.toString() ?? '',
            objektidentitet: selectedOption.objektidentitet
          }
        }));
        handleChange('autocompleteAddress', selectedOption.objektidentitet);
      } else if (!event.currentTarget) {
        setAddressSearchQuery('')
        handleChange('autocompleteAddress', '');
      } else {
        setFormData(prev => ({
          ...prev,
          autocompleteAddress: {
            formatted: '',
            zipCode: '',
            objektidentitet: ''
          }
        }));
        handleChange('autocompleteAddress', addressSearchQuery);
      }
      return;

    } else {
      event.preventDefault();
      value = event.currentTarget.value;
    }

    // Field-specific value processing
    if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'phone') {
      value = value.trim();
    }

    setFormData(prev => ({ ...prev, [fieldType]: value }));

    // Field-specific validation and side effects
    if (fieldType === 'ssn') {
      const validationResult = handleChange(fieldType, value);
      if (validationResult.isValid) {
        getNameFromSSN(value);
      }
    } else {
      handleChange(fieldType, value);
    }
  };

  const addressSearchDebounceRef = useRef<number | undefined>(undefined);

  const handleAddressSearch = (query: string) => {
    setFormData(prev => ({  // reset address immediately on each keystroke
      ...prev,
      autocompleteAddress: {
        formatted: '',
        zipCode: '',
        objektidentitet: ''
      }
    }));
    window.clearTimeout(addressSearchDebounceRef.current);
    addressSearchDebounceRef.current = window.setTimeout(() => {
      setAddressSearchQuery(query);
    }, 300);
  };

  const createBlurHandler = (fieldType: FieldType) => (event: any) => {
    event.preventDefault();
    let value = event.currentTarget.value;

    // Field-specific processing
    if (fieldType === 'autocompleteAddress') {
      // Don't validate on blur for autocompleteAddress, as onBlur fires before the option is selected
      return
    } else if (fieldType === 'houseType' as FieldType) {
      value = formData.houseType || '';
    } else if (fieldType === 'ssn') {
      value = value.replace(/\D/g, '');
    } else if (fieldType === 'phone') {
      value = value.trim();
    }

    const validationResult = handleBlur(fieldType, value);

    // Handle phone number session storage
    if (fieldType === 'phone') {
      if (validationResult.isValid) {
        sessionStorage.setItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY, value);
      } else {
        sessionStorage.removeItem(AppHelper.CUSTOMER_PHONE_NR_SESSION_KEY);
      }
    }
  };

  const handleHouseTypeChange = (value: string) => {
    setFormData(prev => ({ ...prev, houseType: value }));
    handleChange('houseType' as FieldType, value);
  };

  const gotoNextStep = (event: any) => {
    event.preventDefault();

    const transformedFormData = {
      ...formData,
      autocompleteAddress: formData.autocompleteAddress.objektidentitet || addressSearchQuery
    };

    const isFormValid = validateAll(transformedFormData);

    const channels = { hgf: 'HGF', rikshem: 'rikshem' };

    if (isFormValid) {
      const streetName = formData.autocompleteAddress.formatted.split(',')[0];
      const customerInfo = {
        cprNumber: formData.ssn,
        customerName: nameFromService.customerName,
        zipCode: formData.autocompleteAddress.zipCode,
        customerAddress: streetName,
        houseType: formData.houseType,
        ...enonicData?.channel && { channel: channels[enonicData.channel] }
      };
      sessionStorage.setItem('customerInfo', JSON.stringify(customerInfo));

      if (NEXT_PAGE_URL_PATH) {
        window.location.href = window.location.origin + NEXT_PAGE_URL_PATH;
      }
    }
  };

  const getInputValue = (fieldType: FieldType): string => {
    if (fieldType === 'autocompleteAddress') {
      return formData.autocompleteAddress.formatted || addressSearchQuery || '';
    }
    const value = formData[fieldType as keyof FormData];
    return typeof value === 'string' ? value : '';
  };

  const getCprMoreInfoContent = () => {
    return (
      <Text>
        Vi behöver ditt personnummer för att kunna ge dig rätt pris.
        Personnumret ger oss bland annat uppgifter om var du bor och din ålder.
      </Text>
    );
  };

  // To disable phone number in form toggle this to false.
  const showVolontaryPhoneNumber = enonicData?.channel !== 'hgf';

  const homeParams: CalculationWidgetProps = {
    headerText: enonicData.headerText,
    image: enonicData.imageInfo.image,
    smallScreenImage: enonicData.imageInfo.smallScreenImage,
    imageAltText: enonicData.imageInfo.imageAltText,
    inputLists: {
      calculationInputs: [
        ...(enonicData.selectedHouseType === '' ? [{
          type: 'inputRadio',
          wrapperClassName: 'input-group-row',
          inputListInRow: houseTypeOptions.map((option, index) => ({
            id: option.id,
            labelText: option.labelText,
            name: 'houseType', // All radio buttons must share the same name
            onChange: handleHouseTypeChange,
            onBlur: createBlurHandler('houseType' as FieldType),
            value: option.value,
            placeholder: '',
            className: '',
            labelClassName: 'input-label-text',
            invalid: hasFieldError('houseType' as FieldType),
            errorText: getFieldError('houseType' as FieldType),
            helpText: '',
            ref: index === 0 ? refs['houseType' as FieldType] : undefined // Only first radio button gets ref
          }))
        }] : []),
        ...inputFieldDefinitions.map(field => ({
          type: field.fieldType === 'autocompleteAddress' ? 'inputComboBox' as const : 'inputText' as const,
          isHidden: field.fieldType === 'phone' && !showVolontaryPhoneNumber,
          ...(field.fieldType === 'autocompleteAddress' && {
            options: addressComboOptions,
            isLoading: isLoadingAddresses || isFetchingAddresses,
            onSearchChange: handleAddressSearch
          }),
          inputListInRow: [
            {
              ...field.ui,
              onChange: createChangeHandler(field.fieldType),
              onBlur: createBlurHandler(field.fieldType),
              value: getInputValue(field.fieldType),
              invalid: hasFieldError(field.fieldType),
              errorText: getFieldError(field.fieldType),
              ref: refs[field.fieldType],
              moreInfoModal: field.fieldType === 'ssn' ? {
                linkText: 'Varför frågar vi om personnummer?',
                title: 'Personnummer',
                content: getCprMoreInfoContent(),
                closeLabel: 'Stäng hjälptext',
                ariaLabel: 'Hjälp om personnummer',
                wrapperClassName: 'cpr-modal-wrapper',
                ariaDescribedBy: 'cpr-more-info'
              } : undefined
            }
          ]
        })),
      ],
      calculateBtn: {
        btnText: enonicData.calculateBtnLabel,
        onClick: gotoNextStep,
        enableTracking: true,
        disabled: false,
        className: 'calculate-price mt-1'
      }
    },
    additionalInfoLinkText: enonicData.personalDataHandlingInfoLinkText,
    additionalInfoLink: enonicData.personalDataHandlinkUrl,
    footerMessage: enonicData.footerMessage
  };

  return <CalculationWidget {...homeParams} />;
};
